import React, { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Building,
  FileText,
  Settings,
  Shield,
  Users,
  RefreshCw,
  CheckCircle,
  Clock,
  TrendingUp,
  BarChart,
  PieChart,
  XCircle,
  Download,
  BriefcaseBusinessIcon,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getHospitalApplications,
  approveHospitalApplication,
  rejectHospitalApplication,
  getApplicationStats,
} from "../../services/hospitalService";
import {
  getLeadGeneratorApplications,
  approveLeadGeneratorApplication,
  rejectLeadGeneratorApplication,
  getLeadGeneratorStats,
} from "../../services/leadGeneratorService";
import { useAuth } from "../../context/AuthContext";
import AdminApplicationsList from "../admin/AdminApplicationsList";
import AdminSettings from "../admin/AdminSettings";
import { SignOutIconButton } from "../../component/common/SignOutButton";
import NotificationBell from '../../components/common/NotificationBell';
import RoleSwitcher from '../../components/common/RoleSwitcher';
import {
  exportHospitalApplications,
  exportLeadGeneratorApplications,
  exportDashboardStats,
} from "../../services/exportService";

const SystemAdminDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("dashboard");
  const [hospitalApplications, setHospitalApplications] = useState([]);
  const [leadGeneratorApplications, setLeadGeneratorApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [approvalPassword, setApprovalPassword] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    searchTerm: "",
    dateFrom: "",
    dateTo: "",
    state: "",
  });
  const [leadFilters, setLeadFilters] = useState({
    status: "",
    searchTerm: "",
    dateFrom: "",
    dateTo: "",
    state: "",
  });
  const [stats, setStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    approvedHospitals: 0,
    rejectedApplications: 0,
    thisMonthApplications: 0,
    averageApprovalTime: 0,
  });

  const { user } = useAuth();

  // System Admin has limited access - no user management
  const sidebarItems = [
    { id: "dashboard", label: "Dashboard Overview", icon: LayoutDashboard },
    { id: "hospital-approvals", label: "Hospital Approvals", icon: Building },
    { id: "lead-approvals", label: "Lead Approvals", icon: BriefcaseBusinessIcon },
    { id: "applications-review", label: "Applications Review", icon: FileText },
    { id: "system-settings", label: "System Settings", icon: Settings },
  ];

  const statusOptions = [
    { value: "", label: "All Status" },
    {
      value: "pending",
      label: "Pending Review",
      color: "bg-yellow-100 text-yellow-800",
    },
    {
      value: "approved",
      label: "Approved",
      color: "bg-green-100 text-green-800",
    },
    { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-800" },
  ];

  const states = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  const loadDashboardData = async () => {
    try {
      // Load overall statistics
      const statsResult = await getApplicationStats();
      if (statsResult.success) {
        setStats(statsResult.stats);
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (activeSection === "hospital-approvals") {
      loadHospitalApplications();
    } else if (activeSection === "lead-approvals") {
      loadLeadGeneratorApplications();
    }
  }, [activeSection, filters, leadFilters]);

  const loadHospitalApplications = async () => {
    setLoading(true);
    try {
      const result = await getHospitalApplications(filters.status);
      if (result.success) {
        let applications = result.applications;

        // Apply client-side filters
        if (filters.searchTerm) {
          applications = applications.filter(
            (app) =>
              app.hospitalName
                .toLowerCase()
                .includes(filters.searchTerm.toLowerCase()) ||
              app.applicationId
                .toLowerCase()
                .includes(filters.searchTerm.toLowerCase()) ||
              app.representative.name
                .toLowerCase()
                .includes(filters.searchTerm.toLowerCase())
          );
        }

        if (filters.state) {
          applications = applications.filter(
            (app) => app.state === filters.state
          );
        }

        if (filters.dateFrom) {
          applications = applications.filter(
            (app) =>
              new Date(app.submittedAt.toDate()) >= new Date(filters.dateFrom)
          );
        }

        if (filters.dateTo) {
          applications = applications.filter(
            (app) =>
              new Date(app.submittedAt.toDate()) <= new Date(filters.dateTo)
          );
        }

        setHospitalApplications(applications);
      }
    } catch (error) {
      console.error("Error loading hospital applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeadGeneratorApplications = async () => {
    setLoading(true);
    try {
      const result = await getLeadGeneratorApplications(leadFilters.status);
      if (result.success) {
        let applications = result.applications;

        // Apply client-side filters
        if (leadFilters.searchTerm) {
          applications = applications.filter(
            (app) =>
              app.entityName
                .toLowerCase()
                .includes(leadFilters.searchTerm.toLowerCase()) ||
              app.applicationId
                .toLowerCase()
                .includes(leadFilters.searchTerm.toLowerCase()) ||
              app.representative.name
                .toLowerCase()
                .includes(leadFilters.searchTerm.toLowerCase())
          );
        }

        if (leadFilters.state) {
          applications = applications.filter(
            (app) => app.state === leadFilters.state
          );
        }

        if (leadFilters.dateFrom) {
          applications = applications.filter(
            (app) =>
              new Date(app.submittedAt.toDate()) >= new Date(leadFilters.dateFrom)
          );
        }

        if (leadFilters.dateTo) {
          applications = applications.filter(
            (app) =>
              new Date(app.submittedAt.toDate()) <= new Date(leadFilters.dateTo)
          );
        }

        setLeadGeneratorApplications(applications);
      }
    } catch (error) {
      console.error("Error loading lead generator applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const handleLeadFilterChange = (filterType, value) => {
    setLeadFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const resetFilters = () => {
    setFilters({
      status: "",
      searchTerm: "",
      dateFrom: "",
      dateTo: "",
      state: "",
    });
  };

  const resetLeadFilters = () => {
    setLeadFilters({
      status: "",
      searchTerm: "",
      dateFrom: "",
      dateTo: "",
      state: "",
    });
  };

  const handleViewHospitalDetails = (e, application) => {
    // Navigate to hospital details page
    const url = `/admin/hospital-details/${application.applicationId}`;
    
    // Support Ctrl+Click or Cmd+Click to open in new tab
    if (e && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      const fullUrl = `${window.location.origin}${url}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(url);
  };

  const handleViewLeadDetails = (e, application) => {
    // Navigate to lead generator details page
    const url = `/admin/lead-details/${application.applicationId}`;
    
    // Support Ctrl+Click or Cmd+Click to open in new tab
    if (e && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      const fullUrl = `${window.location.origin}${url}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(url);
  };

  const handleApproveHospital = async () => {
    if (!selectedApplication || !approvalPassword) return;

    setActionLoading((prev) => ({ ...prev, [selectedApplication.id]: true }));
    console.log(user);
    try {
      const result = await approveHospitalApplication(
        selectedApplication.applicationId,
        user.uid,
        approvalPassword
      );

      if (result.success) {
        setHospitalApplications((prev) =>
          prev.map((app) =>
            app.id === selectedApplication.id
              ? {
                ...app,
                status: "approved",
                approvedBy: user.uid,
                approvedAt: new Date(),
              }
              : app
          )
        );
        setShowApprovalModal(false);
        setSelectedApplication(null);
        setApprovalPassword("");
        loadDashboardData(); // Refresh stats
        alert("Hospital approved successfully!");
      } else {
        alert("Error approving hospital: " + result.error);
      }
    } catch (error) {
      console.error("Error approving hospital:", error);
      alert("Failed to approve hospital");
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [selectedApplication.id]: false,
      }));
    }
  };

  const handleApproveLeadGenerator = async () => {
    if (!selectedApplication || !approvalPassword) return;

    setActionLoading((prev) => ({ ...prev, [selectedApplication.id]: true }));
    try {
      const result = await approveLeadGeneratorApplication(
        selectedApplication.applicationId,
        user.uid,
        approvalPassword
      );

      if (result.success) {
        setLeadGeneratorApplications((prev) =>
          prev.map((app) =>
            app.id === selectedApplication.id
              ? {
                ...app,
                status: "approved",
                approvedBy: user.uid,
                approvedAt: new Date(),
              }
              : app
          )
        );
        setShowApprovalModal(false);
        setSelectedApplication(null);
        setApprovalPassword("");
        loadDashboardData(); // Refresh stats
        alert("Lead Generator approved successfully!");
      } else {
        alert("Error approving lead generator: " + result.error);
      }
    } catch (error) {
      console.error("Error approving lead generator:", error);
      alert("Failed to approve lead generator");
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [selectedApplication.id]: false,
      }));
    }
  };

  const handleRejectHospital = async () => {
    if (!selectedApplication || !rejectionReason.trim()) return;

    setActionLoading((prev) => ({ ...prev, [selectedApplication.id]: true }));
    try {
      const result = await rejectHospitalApplication(
        selectedApplication.applicationId,
        user.uid,
        rejectionReason
      );

      if (result.success) {
        setHospitalApplications((prev) =>
          prev.map((app) =>
            app.id === selectedApplication.id
              ? {
                ...app,
                status: "rejected",
                rejectedBy: user.uid,
                rejectedAt: new Date(),
                rejectionReason,
              }
              : app
          )
        );
        setShowRejectionModal(false);
        setSelectedApplication(null);
        setRejectionReason("");
        loadDashboardData(); // Refresh stats
        alert("Hospital application rejected.");
      } else {
        alert("Error rejecting hospital: " + result.error);
      }
    } catch (error) {
      console.error("Error rejecting hospital:", error);
      alert("Failed to reject hospital");
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [selectedApplication.id]: false,
      }));
    }
  };

  const handleRejectLeadGenerator = async () => {
    if (!selectedApplication || !rejectionReason.trim()) return;

    setActionLoading((prev) => ({ ...prev, [selectedApplication.id]: true }));
    try {
      const result = await rejectLeadGeneratorApplication(
        selectedApplication.applicationId,
        user.uid,
        rejectionReason
      );

      if (result.success) {
        setLeadGeneratorApplications((prev) =>
          prev.map((app) =>
            app.id === selectedApplication.id
              ? {
                ...app,
                status: "rejected",
                rejectedBy: user.uid,
                rejectedAt: new Date(),
                rejectionReason,
              }
              : app
          )
        );
        setShowRejectionModal(false);
        setSelectedApplication(null);
        setRejectionReason("");
        loadDashboardData(); // Refresh stats
        alert("Lead Generator application rejected.");
      } else {
        alert("Error rejecting lead generator: " + result.error);
      }
    } catch (error) {
      console.error("Error rejecting lead generator:", error);
      alert("Failed to reject lead generator");
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [selectedApplication.id]: false,
      }));
    }
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find((s) => s.value === status);
    return statusOption ? statusOption.color : "bg-gray-100 text-gray-800";
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleExportHospitalApplications = () => {
    try {
      const result = exportHospitalApplications(hospitalApplications, 'hospital_applications_system_admin');
      if (result.success) {
        alert(result.message);
      } else {
        alert('Export failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error exporting hospital applications:', error);
      alert('Failed to export hospital applications');
    }
  };

  const handleExportLeadApplications = () => {
    try {
      const result = exportLeadGeneratorApplications(leadGeneratorApplications, 'lead_generator_applications_system_admin');
      if (result.success) {
        alert(result.message);
      } else {
        alert('Export failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error exporting lead generator applications:', error);
      alert('Failed to export lead generator applications');
    }
  };

  const handleExportDashboardStats = () => {
    try {
      const additionalData = {
        recentActivities: [
          { Activity: 'Hospital Approved', Details: 'City Hospital - Karnataka approved by System Admin', Time: '2 hours ago' },
          { Activity: 'New Application', Details: 'Metro Hospital submitted registration', Time: '4 hours ago' },
          { Activity: 'Application Rejected', Details: 'Regional Clinic - Missing documents', Time: '1 day ago' }
        ],
        monthlyTrends: [
          { Month: 'January', Applications: 45, Approvals: 38, Rejections: 7 },
          { Month: 'February', Applications: 52, Approvals: 44, Rejections: 8 },
          { Month: 'March', Applications: 48, Approvals: 40, Rejections: 8 }
        ]
      };
      
      const result = exportDashboardStats(stats, additionalData, 'system_admin_dashboard_statistics');
      if (result.success) {
        alert(result.message);
      } else {
        alert('Export failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error exporting dashboard statistics:', error);
      alert('Failed to export dashboard statistics');
    }
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">System Admin Dashboard</h1>
        <p className="text-gray-600">
          Overview of system statistics and recent activities
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Applications
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {stats.totalApplications}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-green-600">+12%</span> from last month
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Pending Approval
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {stats.pendingApplications}
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Requires immediate attention
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Approved Hospitals
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {stats.approvedHospitals}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-green-600">+5</span> this week
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Avg. Approval Time
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {stats.averageApprovalTime} days
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-red-600">-0.5</span> days improvement
          </div>
        </div>
      </div>

      {/* Recent Activities & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Recent Activities
          </h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 p-2 rounded-full">
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Hospital Approved
                </p>
                <p className="text-sm text-gray-500">
                  City Hospital - Karnataka approved by System Admin
                </p>
                <p className="text-xs text-gray-400">2 hours ago</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 p-2 rounded-full">
                <FileText className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  New Application
                </p>
                <p className="text-sm text-gray-500">
                  Metro Hospital submitted registration
                </p>
                <p className="text-xs text-gray-400">4 hours ago</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-red-100 p-2 rounded-full">
                <XCircle className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Application Rejected
                </p>
                <p className="text-sm text-gray-500">
                  Regional Clinic - Missing documents
                </p>
                <p className="text-xs text-gray-400">1 day ago</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Quick Actions
          </h3>
          <div className="space-y-3">
            <button
              onClick={() => setActiveSection("hospital-approvals")}
              className="w-full flex items-center justify-start space-x-3 px-4 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all"
            >
              <Building className="h-5 w-5" />
              <span>Review Hospital Applications</span>
            </button>

            <button
              onClick={() => setActiveSection("lead-approvals")}
              className="w-full flex items-center justify-start space-x-3 px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all"
            >
              <TrendingUp className="h-5 w-5" />
              <span>Review Lead Generator Applications</span>
            </button>

            <button
              onClick={() => setActiveSection("applications-review")}
              className="w-full flex items-center justify-start space-x-3 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span>Review Patient Applications</span>
            </button>

            <button 
              onClick={handleExportDashboardStats}
              className="w-full flex items-center justify-start space-x-3 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="h-5 w-5" />
              <span>Generate Reports</span>
            </button>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <BarChart className="h-5 w-5 mr-2 text-teal-500" />
            Application Trends
          </h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
            <p className="text-gray-500">Chart will be implemented here</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <PieChart className="h-5 w-5 mr-2 text-teal-500" />
            Status Distribution
          </h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
            <p className="text-gray-500">Chart will be implemented here</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderHospitalApprovals = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Hospital Approvals
          </h1>
          <p className="text-gray-600">
            Review and approve hospital registration applications
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={handleExportHospitalApplications}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="h-5 w-5" />
            <span>Export</span>
          </button>
          <button
            onClick={loadHospitalApplications}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all"
          >
            <RefreshCw className="h-5 w-5" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <input
              type="text"
              placeholder="Search hospitals..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.state}
              onChange={(e) => handleFilterChange("state", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All States</option>
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <div>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={resetFilters}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Reset Filters</span>
          </button>

          <div className="text-sm text-gray-600">
            {hospitalApplications.length} application(s) found
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-teal-500" />
            <span className="ml-2 text-gray-600">Loading applications...</span>
          </div>
        ) : hospitalApplications.length === 0 ? (
          <div className="text-center py-12">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No applications found
            </h3>
            <p className="text-gray-600">
              No hospital applications match your current filters.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hospital Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Information
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Facilities
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {hospitalApplications.map((application) => (
                  <tr key={application.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-3">
                          <Building className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {application.hospitalName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {application.applicationId}
                          </div>
                          <div className="text-xs text-gray-400">
                            {application.numberOfBeds} beds
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center mb-1">
                          <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                          {application.city}, {application.state}
                        </div>
                        <div className="flex items-center mb-1">
                          <Phone className="h-3 w-3 mr-1 text-gray-400" />
                          {application.phone}
                        </div>
                        <div className="flex items-center">
                          <Mail className="h-3 w-3 mr-1 text-gray-400" />
                          {application.email}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="mb-1">
                          {application.facilities?.length || 0} facilities
                        </div>
                        <div>
                          {application.specialities?.length || 0} specialities
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          application.status
                        )}`}
                      >
                        {application.status.charAt(0).toUpperCase() +
                          application.status.slice(1)}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(application.submittedAt)}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => handleViewHospitalDetails(e, application)}
                          className="text-teal-600 hover:text-teal-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>

                        {application.status === "pending" && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedApplication(application);
                                setShowApprovalModal(true);
                              }}
                              disabled={actionLoading[application.id]}
                              className="text-green-600 hover:text-green-900"
                              title="Approve"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>

                            <button
                              onClick={() => {
                                setSelectedApplication(application);
                                setShowRejectionModal(true);
                              }}
                              disabled={actionLoading[application.id]}
                              className="text-red-600 hover:text-red-900"
                              title="Reject"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderLeadGeneratorApprovals = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Lead Generator Approvals
          </h1>
          <p className="text-gray-600">
            Review and approve lead generator registration applications
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={handleExportLeadApplications}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="h-5 w-5" />
            <span>Export</span>
          </button>
          <button
            onClick={loadLeadGeneratorApplications}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all"
          >
            <RefreshCw className="h-5 w-5" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <input
              type="text"
              placeholder="Search lead generators..."
              value={leadFilters.searchTerm}
              onChange={(e) => handleLeadFilterChange("searchTerm", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <div>
            <select
              value={leadFilters.status}
              onChange={(e) => handleLeadFilterChange("status", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={leadFilters.state}
              onChange={(e) => handleLeadFilterChange("state", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All States</option>
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div>
            <input
              type="date"
              value={leadFilters.dateFrom}
              onChange={(e) => handleLeadFilterChange("dateFrom", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <div>
            <input
              type="date"
              value={leadFilters.dateTo}
              onChange={(e) => handleLeadFilterChange("dateTo", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={resetLeadFilters}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Reset Filters</span>
          </button>

          <div className="text-sm text-gray-600">
            {leadGeneratorApplications.length} application(s) found
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-teal-500" />
            <span className="ml-2 text-gray-600">Loading applications...</span>
          </div>
        ) : leadGeneratorApplications.length === 0 ? (
          <div className="text-center py-12">
            <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No applications found
            </h3>
            <p className="text-gray-600">
              No lead generator applications match your current filters.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Information
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Services Required
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leadGeneratorApplications.map((application) => (
                  <tr key={application.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-3">
                          <TrendingUp className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {application.entityName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {application.applicationId}
                          </div>
                          <div className="text-xs text-gray-400">
                            CIN: {application.cinNumber}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center mb-1">
                          <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                          {application.city}, {application.state}
                        </div>
                        <div className="flex items-center mb-1">
                          <Phone className="h-3 w-3 mr-1 text-gray-400" />
                          {application.phone}
                        </div>
                        <div className="flex items-center">
                          <Mail className="h-3 w-3 mr-1 text-gray-400" />
                          {application.email}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="mb-1">
                          {application.servicesRequired?.length || 0} services
                        </div>
                        <div className="text-xs text-gray-500">
                          {application.servicesRequired?.slice(0, 2).join(", ")}
                          {application.servicesRequired?.length > 2 && "..."}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          application.status
                        )}`}
                      >
                        {application.status.charAt(0).toUpperCase() +
                          application.status.slice(1)}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(application.submittedAt)}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => handleViewLeadDetails(e, application)}
                          className="text-teal-600 hover:text-teal-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>

                        {application.status === "pending" && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedApplication(application);
                                setShowApprovalModal(true);
                              }}
                              disabled={actionLoading[application.id]}
                              className="text-green-600 hover:text-green-900"
                              title="Approve"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>

                            <button
                              onClick={() => {
                                setSelectedApplication(application);
                                setShowRejectionModal(true);
                              }}
                              disabled={actionLoading[application.id]}
                              className="text-red-600 hover:text-red-900"
                              title="Reject"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return renderDashboard();
      case "hospital-approvals":
        return renderHospitalApprovals();
      case "lead-approvals":
        return renderLeadGeneratorApprovals();
      case "applications-review":
        return <AdminApplicationsList />;
      case "system-settings":
        return <AdminSettings />;
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <img src={require('../../assets/samhealth.png')} alt="SamHealth" className="h-8 w-auto object-contain" />
              <p className="text-sm text-teal-600">System Admin Portal</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <NotificationBell />
            <div className="text-right">
              <p className="text-sm font-medium text-gray-800">System Admin</p>
              <p className="text-xs text-gray-600">{user?.email}</p>
            </div>
            <RoleSwitcher buttonClassName="border-gray-300 hover:border-teal-400 hover:text-teal-700" />
            {/* Removed decorative header icon */}
            <SignOutIconButton
              size="sm"
              showConfirmModal={true}
              className="hover:bg-red-100"
            />
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 h-screen bg-white shadow-lg border-r border-gray-200">
          <nav className="mt-8">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center px-6 py-3 text-left transition-colors duration-200 ${
                    isActive
                      ? "bg-teal-50 text-teal-600 border-r-2 border-teal-500"
                      : "text-gray-700 hover:bg-gray-50 hover:text-teal-600"
                  }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">{renderContent()}</main>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Approve {selectedApplication.hospitalName ? 'Hospital' : 'Lead Generator'}
              </h3>
              <button
                onClick={() => setShowApprovalModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <h4 className="font-medium text-green-800">
                      Approving: {selectedApplication.hospitalName || selectedApplication.entityName}
                    </h4>
                    <p className="text-sm text-green-600">
                      This action will create login credentials for the {selectedApplication.hospitalName ? 'hospital' : 'lead generator'}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Password *
                </label>
                <input
                  type="password"
                  value={approvalPassword}
                  onChange={(e) => setApprovalPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder={`Enter password for ${selectedApplication.hospitalName ? 'hospital' : 'lead generator'} account`}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This password will be sent to the {selectedApplication.hospitalName ? 'hospital' : 'lead generator'} for their account access
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={selectedApplication.hospitalName ? handleApproveHospital : handleApproveLeadGenerator}
                disabled={
                  !approvalPassword.trim() ||
                  actionLoading[selectedApplication.id]
                }
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {actionLoading[selectedApplication.id] ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin inline" />
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2 inline" />
                    Approve {selectedApplication.hospitalName ? 'Hospital' : 'Lead Generator'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Reject {selectedApplication.hospitalName ? 'Hospital' : 'Lead Generator'}
              </h3>
              <button
                onClick={() => setShowRejectionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-3">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <div>
                    <h4 className="font-medium text-red-800">
                      Rejecting: {selectedApplication.hospitalName || selectedApplication.entityName}
                    </h4>
                    <p className="text-sm text-red-600">
                      Please provide a clear reason for rejection
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  rows="4"
                  placeholder="Provide detailed reason for rejection..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This reason will be communicated to the {selectedApplication.hospitalName ? 'hospital' : 'lead generator'}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRejectionModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={selectedApplication.hospitalName ? handleRejectHospital : handleRejectLeadGenerator}
                disabled={
                  !rejectionReason.trim() ||
                  actionLoading[selectedApplication.id]
                }
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {actionLoading[selectedApplication.id] ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin inline" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2 inline" />
                    Reject {selectedApplication.hospitalName ? 'Hospital' : 'Lead Generator'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemAdminDashboard;
